
import React, { useState, useEffect } from "react";
import OpportunityCard from "./OpportunityCard";
import { DevTool } from "@/components/dashboard/roadmap/DevToolSelect";

export interface Opportunity {
  title: string;
  description: string;
  dataPoints: number;
  percentage: number;
  priority?: number;
  id?: string;
}

interface OpportunitySectionProps {
  title: string;
  color: string;
  opportunities: Opportunity[];
  startIndex: number;
  borderBottom?: boolean;
  devTool?: DevTool;
  capacity?: number;
}

const OpportunitySection = ({ 
  title, 
  color, 
  opportunities, 
  startIndex,
  borderBottom = true,
  devTool = "jira",
  capacity
}: OpportunitySectionProps) => {
  const [visibleOpportunities, setVisibleOpportunities] = useState<Opportunity[]>(opportunities);
  const [currentDevTool, setCurrentDevTool] = useState<DevTool>(devTool);
  
  useEffect(() => {
    // Update visible opportunities when the original opportunities change
    setVisibleOpportunities(opportunities);
  }, [opportunities]);
  
  useEffect(() => {
    // Listen for dev tool changes
    const handleDevToolChange = (e: CustomEvent) => {
      if (e.detail) {
        console.log(`OpportunitySection received devTool change: ${e.detail}`);
        setCurrentDevTool(e.detail as DevTool);
      }
    };

    window.addEventListener('devToolChange', handleDevToolChange as EventListener);
    
    return () => {
      window.removeEventListener('devToolChange', handleDevToolChange as EventListener);
    };
  }, []);
  
  useEffect(() => {
    // Update current dev tool from props
    if (devTool !== currentDevTool) {
      setCurrentDevTool(devTool);
    }
  }, [devTool, currentDevTool]);

  return (
    <div className={`mb-8 pb-8 ${borderBottom ? 'border-b border-gray-200' : ''}`}>
      <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
        <span className="text-gray-700 uppercase">{title}</span>
      </h3>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-5">
        {visibleOpportunities.map((opportunity, index) => (
          <div key={opportunity.id}>
            <OpportunityCard
              opportunity={opportunity}
              number={startIndex + index + 1}
              devTool={currentDevTool}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default OpportunitySection;
